/**
 * ReportTemplateOrderingList Component
 *
 * Drag-and-drop enabled list for ordering and excluding report templates
 */

import React, { useMemo, useCallback, useEffect } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { AlertCircle, Loader2 } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import ReportTemplateItem from './ReportTemplateItem'
import { useReportTemplates } from './hooks/useReportTemplates'
import {
  getOrderedTemplatesWithSettings,
  toggleReportIgnored,
  updateSortKeysAfterDrag,
  mergeSettingsWithTemplates
} from './utils'
import type { ReportTemplateOrderingListProps } from './types'

const ReportTemplateOrderingList: React.FC<ReportTemplateOrderingListProps> = ({
  reportSettings,
  onSettingsChange,
  disabled = false,
  headerAction
}) => {
  const { templates, isLoading, error, refetch } = useReportTemplates()

  // Sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // Prevent accidental drags
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Merge existing settings with fetched templates
  useEffect(() => {
    if (templates.length > 0) {
      const mergedSettings = mergeSettingsWithTemplates(reportSettings, templates)
      // Only update if there are actual changes
      if (JSON.stringify(mergedSettings) !== JSON.stringify(reportSettings)) {
        onSettingsChange(mergedSettings)
      }
    }
  }, [templates])

  // Get ordered templates with their settings
  const orderedTemplates = useMemo(() => {
    return getOrderedTemplatesWithSettings(templates, reportSettings)
  }, [templates, reportSettings])

  // Get template keys in current order for SortableContext
  const templateKeys = useMemo(() => {
    return orderedTemplates.map(item => item.template.key)
  }, [orderedTemplates])

  // Handle drag end event
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        return
      }

      const oldIndex = templateKeys.indexOf(active.id as string)
      const newIndex = templateKeys.indexOf(over.id as string)

      if (oldIndex === -1 || newIndex === -1) {
        console.warn('Invalid drag operation: template not found')
        return
      }

      // Update sort keys based on new order
      const updatedSettings = updateSortKeysAfterDrag(
        reportSettings,
        active.id as string,
        newIndex
      )

      onSettingsChange(updatedSettings)
    },
    [templateKeys, reportSettings, onSettingsChange]
  )

  // Handle toggle ignored status
  const handleToggleIgnored = useCallback(
    (templateId: string) => {
      const updatedSettings = toggleReportIgnored(reportSettings, templateId)
      onSettingsChange(updatedSettings)
    },
    [reportSettings, onSettingsChange]
  )

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2
          size={24}
          className="animate-spin"
          style={{ color: designTokens.colors.primary.DEFAULT }}
        />
        <span
          className="ml-3"
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.gray[5]
          }}
        >
          보고서 템플릿을 불러오는 중...
        </span>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 rounded border border-red-200 bg-red-50">
        <AlertCircle size={20} style={{ color: designTokens.colors.error.DEFAULT }} />
        <div className="flex-1">
          <p
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              fontWeight: 600,
              color: designTokens.colors.error.DEFAULT
            }}
          >
            오류가 발생했습니다
          </p>
          <p
            className="mt-1"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              color: designTokens.colors.gray[5]
            }}
          >
            {error}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-3 py-1.5 rounded transition-colors"
          style={{
            backgroundColor: designTokens.colors.error.DEFAULT,
            color: designTokens.colors.white,
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-small'][0],
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer'
          }}
        >
          재시도
        </button>
      </div>
    )
  }

  // Empty state
  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.gray[4]
          }}
        >
          보고서 템플릿이 없습니다.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3 mt-3">
      {/* Header */}
      <div className="flex items-center gap-3 pb-2 border-b" style={{ borderColor: designTokens.colors.gray[1] }}>
        <h4
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            fontWeight: 600,
            color: designTokens.colors.gray[6]
          }}
        >
          보고서 설정
        </h4>
        {headerAction}
      </div>

      {/* Draggable List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={templateKeys} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {orderedTemplates.map((item, index) => (
              <ReportTemplateItem
                key={item.template.key}
                template={item.template}
                setting={item.setting}
                index={index}
                onToggleIgnored={handleToggleIgnored}
                disabled={disabled}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

export default ReportTemplateOrderingList
